import { useCallback } from 'react';
import '../../node_modules/tslib/tslib.es6.js';
import { track } from '../../analytics/AnalyticsService.js';
import 'branch-sdk';
import '../../analytics/consent.js';
import { AnalyticsTrackingEvent } from '../../analytics/events.js';
import '../../analytics/externalTracking.js';
import '../../analytics/fingerprint.js';
import '../../analytics/types.js';
import '../../analytics/metaPixel.js';
import { useConciergeContext } from '../ConciergeContextProvider.js';

/**
 * Hook to track product clicks in the concierge.
 *
 * Uses the ConciergeContext to get the current conversationId.
 * Tracking is non-blocking (fire-and-forget) to not delay navigation.
 *
 * @returns A function to track product clicks
 */
const useProductClickTracking = () => {
    const { conversationId } = useConciergeContext();
    const trackProductClick = useCallback((params) => {
        // Don't track if no conversation (shouldn't happen but be safe)
        if (!conversationId) {
            console.warn('[Concierge] Cannot track product click: no conversationId');
            return;
        }
        const properties = {
            conversationId,
            stacklineSku: params.stacklineSku,
            productTitle: params.productTitle,
            clickSource: params.clickSource,
            productPosition: params.productPosition,
            retailerId: params.retailerId,
            retailPrice: params.retailPrice,
            hasReward: params.hasReward,
            rewardAmount: params.rewardAmount,
            messageIndex: params.messageIndex,
        };
        // Fire-and-forget - don't await, don't block
        track(AnalyticsTrackingEvent.CONCIERGE_PRODUCT_CLICK, properties).catch((err) => {
            // Silently log errors - tracking should never break the app
            console.warn('[Concierge] Failed to track product click:', err);
        });
    }, [conversationId]);
    return { trackProductClick, conversationId };
};
/**
 * Standalone function to track product clicks without requiring hooks.
 * Useful for class components or when context is not available.
 *
 * @param conversationId - The current conversation ID
 * @param params - The product click parameters
 */
const trackProductClick = (conversationId, params) => {
    if (!conversationId) {
        console.warn('[Concierge] Cannot track product click: no conversationId');
        return;
    }
    const properties = {
        conversationId,
        stacklineSku: params.stacklineSku,
        productTitle: params.productTitle,
        clickSource: params.clickSource,
        productPosition: params.productPosition,
        retailerId: params.retailerId,
        retailPrice: params.retailPrice,
        hasReward: params.hasReward,
        rewardAmount: params.rewardAmount,
        messageIndex: params.messageIndex,
    };
    // Fire-and-forget
    track(AnalyticsTrackingEvent.CONCIERGE_PRODUCT_CLICK, properties).catch((err) => {
        console.warn('[Concierge] Failed to track product click:', err);
    });
};

export { trackProductClick, useProductClickTracking };
//# sourceMappingURL=useProductClickTracking.js.map
